import React, { useEffect } from 'react';
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigationType,
  useParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { detectBrowserLang, isLanguageCode } from './languages';
import Layout from '../layout/Layout';
import Seo from '../components/Seo';

// Reset scroll on every PUSH navigation (clicking a link, calling
// navigate()). POP (back/forward) is left alone so the browser
// restores the previous scroll position naturally. Anchor jumps
// inside a page (the Privacy sidebar's `#section` links) keep
// `pathname` constant, so they're untouched too.
const ScrollToTopOnNavigate: React.FC = () => {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType === 'POP') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, navType]);
  return null;
};

// Top-level app routes (the segment after /:lang). Used to tell an
// un-prefixed URL ("/locations") apart from a bad language ("/xx").
const KNOWN_ROUTES = new Set([
  'login',
  'forgot-password',
  'reset-password',
  'register',
  'verify-email',
  'locations',
  'my-stamps',
  'profile',
  'privacy',
  'terms',
]);

// Validates the /:lang segment, keeps i18next + <html lang> in sync, and
// renders the shared <Layout>. Unknown languages fall through to the
// redirector below.
const LangLayout: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (isLanguageCode(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    if (isLanguageCode(lang)) {
      document.documentElement.lang = lang;
    }
  }, [lang, i18n]);

  if (!isLanguageCode(lang)) {
    const target = detectBrowserLang();
    const segments = location.pathname.split('/').filter(Boolean);
    // First segment is a real route ("/locations") → URL was simply
    // un-prefixed; keep the path and prepend the detected language.
    // Otherwise it was a bad language ("/xx/locations") → drop it.
    const rest =
      segments[0] && KNOWN_ROUTES.has(segments[0])
        ? segments
        : segments.slice(1);
    const path = rest.length ? `/${rest.join('/')}` : '';
    return (
      <Navigate to={`/${target}${path}${location.search}`} replace />
    );
  }

  return (
    <Layout>
      <ScrollToTopOnNavigate />
      {/* Baseline head for every page; pages may render their own
          <Seo> to override title/description. */}
      <Seo />
      <Outlet />
    </Layout>
  );
};

// Bare root "/" → detected language home.
export const RootRedirect: React.FC = () => {
  const target = detectBrowserLang();
  const { search } = useLocation();
  return <Navigate to={`/${target}${search}`} replace />;
};

export default LangLayout;
